/**
 * ProjectDetails (#351) — shown in the detail column when a PROJECT node is selected
 * in the tree (previously that showed nothing). Summarises the project: identity + org,
 * KPI numbers, members & roles, a status breakdown, coverage, and a review "breakdown"
 * of the items that need attention (proposed archives, open review comments, unverified
 * leaves), each of which opens the item. Data is assembled from existing endpoints
 * (iamApi.getProject / projectMembers + requirementsApi.getItems/getProjectFeedback) —
 * no new backend surface required.
 */
import * as React from 'react';
import { Box } from '@mui/material';
import { requirementsApi } from '../services/api';
import iamApi from '../services/iamApi';
import BreakdownTab from '../components/itemDetails/BreakdownTab';
import ProjectActionBar from '../components/project/ProjectActionBar';
import useReviewActivity from '../hooks/useReviewActivity';

// #428: the status/tone/flatten helpers this view ran over the full eager tree
// now live server-side (core/requirements/project_summary.py, GET /items/summary)
// — the view renders the returned aggregates; per-row tone derives from `kind`.
const KIND_TONE = {
  'Proposed archive': 'var(--error)',
  'Open review comments': 'var(--warning)',
  'Unverified': 'var(--warning)',
};

function initials(nameOrEmail) {
  const s = String(nameOrEmail || '').trim();
  if (!s) return '?';
  const parts = s.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

function Bar({ label, colorVar, value, total }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 34px', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 12.5, color: 'var(--text-2, #9aa2b8)' }}>{label}</span>
      <span style={{ height: 8, borderRadius: 6, background: 'var(--surface-2, rgba(127,127,127,.15))', overflow: 'hidden' }}>
        <span style={{ display: 'block', height: '100%', width: `${pct}%`, background: colorVar, borderRadius: 6 }} />
      </span>
      <span style={{ fontSize: 12, textAlign: 'right', color: 'var(--text-3, #6b7385)', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}

function Kpi({ label, value, tone, cap }) {
  return (
    <div style={{ background: 'var(--surface-1, rgba(127,127,127,.08))', border: '1px solid var(--border, rgba(127,127,127,.2))', borderRadius: 12, padding: '13px 14px' }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-3, #6b7385)' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4, color: tone || 'inherit', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {cap && <div style={{ fontSize: 12, color: 'var(--text-2, #9aa2b8)', marginTop: 2 }}>{cap}</div>}
    </div>
  );
}

export default function ProjectDetails({ projectId, onNavigateToItem, refreshTree }) {
  const [project, setProject] = React.useState(null);
  const [members, setMembers] = React.useState([]);
  const [items, setItems] = React.useState(null); // #428: the server summary
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  // #381: bumped by ProjectActionBar's onChanged (new item / copy / archive) so
  // the panel's KPIs/breakdown re-fetch without a full navigation.
  const [reloadKey, setReloadKey] = React.useState(0);
  // #410: the full breakdown is heavy (one review fetch per top-level item), so it is
  // LAZY — not loaded on every project click. The user expands it on demand. Reset when
  // the selected project changes so a new project doesn't inherit the last one's state.
  const [showBreakdown, setShowBreakdown] = React.useState(false);
  React.useEffect(() => { setShowBreakdown(false); }, [projectId]);

  // N-21 / REQ-6133 (#609): live refresh for each root's Breakdown panel below.
  // Unlike BreakdownTab itself (and unlike the tree's `activeProject`), THIS view
  // already receives its one project id as a prop — no derivation needed.
  const { changeCount: reviewChangeCount } = useReviewActivity(projectId ? [projectId] : []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      iamApi.getProject(projectId).catch(() => null),
      iamApi.projectMembers(projectId).catch(() => []),
      // #428: bounded server aggregates instead of the whole eager tree
      // (the open-comments set now feeds the summary server-side too).
      requirementsApi.getItemsSummary(projectId).catch(() => null),
    ]).then(([proj, mem, summary]) => {
      if (cancelled) return;
      setProject(proj);
      setMembers(Array.isArray(mem) ? mem : []);
      setItems(summary);
      setLoading(false);
    }).catch((e) => { if (!cancelled) { setError(String(e.message || e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [projectId, reloadKey]);

  const stats = React.useMemo(() => {
    if (!items) {
      return { total: 0, containers: 0, verified: 0, verifyPct: 0,
               byStatus: { draft: 0, approved: 0, implemented: 0, verified: 0, archive_proposed: 0, other: 0 },
               needsAttention: [], roots: [], itemCount: 0 };
    }
    return {
      total: items.totals.leaves,
      containers: items.totals.containers,
      verified: items.verified,
      verifyPct: items.verify_pct,
      byStatus: items.by_status,
      needsAttention: items.needs_attention.rows,
      roots: items.roots,
      itemCount: items.totals.items,
    };
  }, [items]);

  if (loading) {
    return <div className="dbody"><div className="t-body muted" style={{ padding: 12 }}>Loading project…</div></div>;
  }
  if (error) {
    return <div className="dbody"><div className="t-body" style={{ padding: 12, color: 'var(--error)' }}>Could not load project: {error}</div></div>;
  }

  const name = (project && project.name) || 'Project';
  const prefix = project && (project.prefix || project.key);

  return (
    <div className="dbody" data-testid="project-details" style={{ height: '100%', overflow: 'auto', padding: 16 }}>
      <div style={{ fontSize: 12, color: 'var(--text-3, #6b7385)', letterSpacing: '.02em' }}>Project</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>{name}</h2>
        {prefix && <span className="chip" style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: 'var(--accent-soft, rgba(120,110,240,.15))', color: 'var(--accent)' }}>{prefix}</span>}
        <span className="chip" style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, border: '1px solid var(--border, rgba(127,127,127,.25))', color: 'var(--text-2, #9aa2b8)' }}>{stats.containers} containers</span>
      </div>

      {/* #381: project actions (New item / Copy / Archive / Export) — the same set
          the requirements-tree project right-click menu offers, reachable from this
          project overview too. */}
      <div style={{ marginTop: 12 }}>
        <ProjectActionBar
          projectId={projectId}
          projectName={name}
          onError={(msg) => setError(msg)}
          onChanged={() => { setReloadKey((k) => k + 1); if (refreshTree) refreshTree(); }}
        />
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 12, margin: '16px 0' }}>
        <Kpi label="Requirements" value={stats.total} cap={`${stats.containers} containers`} />
        <Kpi label="Verification" value={`${stats.verifyPct}%`} tone="var(--success)" cap={`${stats.verified} of ${stats.total} verified`} />
        <Kpi label="Coverage gaps" value={stats.total - stats.verified} tone="var(--warning)" cap="unverified" />
        <Kpi label="Needs review" value={stats.needsAttention.length} tone="var(--warning)" cap="outstanding" />
      </div>

      {/* Members & roles */}
      <div style={{ marginBottom: 18 }}>
        <div className="t-over" style={{ marginBottom: 8 }}>Members &amp; roles</div>
        {members.length === 0 ? (
          <div className="t-body muted">No members assigned.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {members.map((m) => (
                <tr key={m.user_id} style={{ borderTop: '1px solid var(--border, rgba(127,127,127,.15))' }}>
                  {/* aria-label (#943, jsx-a11y/control-has-associated-label): the member's
                      name sits three levels down inside a flex row, past the depth the
                      rule reads, so the cell had no accessible name of its own. */}
                  <td style={{ padding: '7px 4px' }} aria-label={m.display_name || m.email || m.user_id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                      <span style={{ width: 26, height: 26, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 700, color: '#fff', background: 'var(--accent)', flex: 'none' }}>
                        {initials(m.display_name || m.email || m.user_id)}
                      </span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{m.display_name || m.email || m.user_id}</div>
                        {m.email && <div style={{ fontSize: 11.5, color: 'var(--text-2, #9aa2b8)' }}>{m.email}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '7px 4px', textAlign: 'right' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, border: '1px solid var(--border, rgba(127,127,127,.25))', color: 'var(--text-2, #9aa2b8)' }}>{m.role}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Status breakdown */}
      <div style={{ marginBottom: 18 }}>
        <div className="t-over" style={{ marginBottom: 10 }}>Status breakdown</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <Bar label="Draft" colorVar="var(--text-3, #6b7385)" value={stats.byStatus.draft} total={stats.total} />
          <Bar label="Approved" colorVar="var(--accent)" value={stats.byStatus.approved} total={stats.total} />
          <Bar label="Implemented" colorVar="var(--warning)" value={stats.byStatus.implemented} total={stats.total} />
          <Bar label="Verified" colorVar="var(--success)" value={stats.byStatus.verified} total={stats.total} />
          <Bar label="Archive proposed" colorVar="var(--error)" value={stats.byStatus.archive_proposed} total={stats.total} />
        </div>
      </div>

      {/* Breakdown — needs attention */}
      <div>
        <div className="t-over" style={{ marginBottom: 8 }}>Breakdown — needs attention</div>
        {stats.needsAttention.length === 0 ? (
          <div className="t-body muted">Nothing outstanding — every requirement is verified.</div>
        ) : (
          <div style={{ border: '1px solid var(--border, rgba(127,127,127,.2))', borderRadius: 12, overflow: 'hidden' }}>
            {stats.needsAttention.slice(0, 50).map((row) => (
              <button
                key={row.id}
                type="button"
                data-testid={`project-attention-${row.id}`}
                aria-label={`Open ${row.kind} ${row.title || 'Untitled'}`}
                onClick={() => onNavigateToItem && onNavigateToItem(row.id)}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 11, padding: '10px 12px', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', borderTop: '1px solid var(--border, rgba(127,127,127,.12))', cursor: 'pointer', color: 'inherit' }}
              >
                <span style={{ width: 9, height: 9, borderRadius: '50%', marginTop: 5, flex: 'none', background: KIND_TONE[row.kind] || 'var(--warning)' }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 13.5 }}>{row.title || 'Untitled'}</span>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--text-2, #9aa2b8)', marginTop: 2 }}>{row.kind}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* #351/#410: the WHOLE breakdown view — the actual PR-style review (approvals,
          comment threads, rollups) for the project's items, not just the triage list
          above. One BreakdownTab per top-level item. LAZY (#410): rendered only after
          the user asks, so clicking a project doesn't fire N review fetches by default. */}
      <div style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <div className="t-over">Full breakdown</div>
          {stats.itemCount > 0 && (
            <button
              type="button"
              className="btn ghost sm"
              data-testid="project-breakdown-toggle"
              onClick={() => setShowBreakdown((s) => !s)}
            >
              {showBreakdown ? 'Hide breakdown' : 'Load breakdown'}
            </button>
          )}
        </div>
        {stats.itemCount === 0 ? (
          <div className="t-body muted">No items in this project yet.</div>
        ) : !showBreakdown ? (
          <div className="t-body muted" data-testid="project-breakdown-collapsed">
            Review the whole project in one go — load the approvals, comment threads and
            rollups for every top-level item.
          </div>
        ) : (
          stats.roots.map((root) => (
            <Box
              key={root.id}
              data-testid={`project-breakdown-${root.id}`}
              sx={{ height: 560, border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mb: 2 }}
            >
              <BreakdownTab
                itemId={root.id}
                onNavigateToItem={onNavigateToItem}
                reviewChangeCount={reviewChangeCount}
              />
            </Box>
          ))
        )}
      </div>
    </div>
  );
}
